import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, AlertCircle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export default function TestlabPremiumEchoPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("Säg 'OK' om du är nåbar.");
  const [dep, setDep] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);

  async function ask() {
    setLoading(true); 
    setErr(null); 
    setOut(null);
    
    try {
      const res = await fetch("https://bijyphjgqkqmsrzhiqin.supabase.co/functions/v1/testlab-premium-echo", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          query: q,
          deployment: dep || undefined,
          max_completion_tokens: 256
        })
      });
      
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setOut(json);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <div className="container mx-auto p-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/testlab")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till Testlab
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Premium Echo</h1>
            <p className="text-muted-foreground">
              Direkttest av Azure OpenAI premium deployment via Responses API
            </p>
          </div>
          <Badge variant="secondary" className="ml-auto">TESTLAB</Badge>
        </div>

        <div className="grid gap-6">
          {/* Input Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Skicka fråga till premium-modell
              </CardTitle>
              <CardDescription>
                Testar direkt kommunikation med Azure OpenAI Responses API utan fallback eller regelprocessning.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="query">Fråga</Label>
                <Textarea
                  id="query"
                  placeholder="Skriv din fråga här..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deployment">Deployment (valfritt)</Label>
                <Input
                  id="deployment"
                  placeholder="t.ex. gpt-5 eller gpt-4.1"
                  value={dep}
                  onChange={(e) => setDep(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Lämna tomt för standard deployment (gpt-5)
                </p>
              </div>

              <Button
                onClick={ask}
                disabled={loading || !q.trim()}
                className="w-full gap-2"
                size="lg"
              >
                <Send className="h-4 w-4" />
                {loading ? "Skickar..." : "Skicka till premium"}
              </Button>
            </CardContent>
          </Card>

          {/* Error Display */}
          {err && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <p className="font-medium text-destructive">Fel uppstod</p>
                    <pre className="text-sm bg-destructive/10 p-3 rounded border text-destructive whitespace-pre-wrap">
                      {err}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Display */}
          {out && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="h-5 w-5" />
                  Svar från {out.deployment}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-emerald-100/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Deployment</p>
                    <p className="text-sm text-emerald-600">{out.deployment}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Latency</p>
                    <p className="text-sm text-emerald-600">{out.latencyMs} ms</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Tokens</p>
                    <p className="text-sm text-emerald-600">
                      {out.usage ? `${out.usage.total_tokens} (${out.usage.prompt_tokens}+${out.usage.completion_tokens})` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Response */}
                <div>
                  <Label className="text-emerald-700 font-medium">Svar:</Label>
                  <div className="mt-2 p-4 bg-white border border-emerald-200 rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                      {out.answer}
                    </pre>
                  </div>
                </div>

                {/* Usage Details */}
                {out.usage && (
                  <div>
                    <Label className="text-emerald-700 font-medium">Usage detaljer:</Label>
                    <pre className="mt-2 p-3 bg-emerald-100/30 border border-emerald-200 rounded text-xs text-emerald-800">
                      {JSON.stringify(out.usage, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}